// @ts-nocheck
/**
 * Top Picks Cart Custom Element
 *
 * An Embla carousel showing recommended products based on cart items.
 * Fetches product recommendations for each cart item and displays them
 * in a free-scroll carousel.
 *
 * @example
 * <rt-top-picks-cart data-product-ids="123456789,987654321">
 *   <div class="rt-top-picks-cart__viewport">
 *     <div class="rt-top-picks-cart__container"></div>
 *   </div>
 * </rt-top-picks-cart>
 */

/**
 * Top Picks Cart Custom Element
 * Fetches product recommendations for cart items and displays them in a carousel.
 *
 * @fires rt-top-picks-cart:loaded - When recommendations are loaded
 * @fires rt-top-picks-cart:error - When an error occurs
 * @fires rt-top-picks-cart:add-to-cart - When a product is added to cart
 */
class RtTopPicksCart extends HTMLElement {
  static tagName = 'rt-top-picks-cart';

  /** @type {AbortController | null} */
  _abortController = null;

  /** @type {Map<string, object[]>} */
  _recommendationsCache = new Map();

  /** @type {number} */
  LIMIT_PER_PRODUCT = 4;

  /** @type {boolean} */
  _isDragging = false;

  /** @type {number} */
  _startX = 0;

  /** @type {number} */
  _scrollLeft = 0;

  constructor() {
    super();
    this._productIds = [];
    this._recommendations = [];
    this._isLoading = false;
  }

  static get observedAttributes() {
    return ['data-product-ids'];
  }

  connectedCallback() {
    this._parseProductIds();
    this._init();
  }

  disconnectedCallback() {
    this._abortController?.abort();
    this._cleanupDragScroll();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'data-product-ids' && oldValue !== newValue && oldValue !== null) {
      this._parseProductIds();
      this._init();
    }
  }

  /**
   * Parse product IDs from the data-product-ids attribute
   */
  _parseProductIds() {
    const productsAttr = this.getAttribute('data-product-ids') || '';
    this._productIds = productsAttr
      .split(',')
      .map((id) => id.trim())
      .filter((id) => id.length > 0);
  }

  // Format price
  _formatPrice(cents, currency = 'USD') {
    const price = cents / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    })
      .format(price)
      .replace(/\.00$/, '');
  }

  /**
   * Initialize the carousel by fetching recommendations
   */
  async _init() {
    if (this._productIds.length === 0) {
      this._hide();
      return;
    }

    this._setLoading(true);

    try {
      await this._fetchAllRecommendations();

      if (this._recommendations.length === 0) {
        this._hide();
        return;
      }

      this._show();
      this._renderProducts();
      this._initDragScroll();

      this.dispatchEvent(
        new CustomEvent('rt-top-picks-cart:loaded', {
          bubbles: true,
          detail: { recommendations: this._recommendations },
        })
      );
    } catch (error) {
      console.error('Error loading top picks recommendations:', error);
      this._hide();

      this.dispatchEvent(
        new CustomEvent('rt-top-picks-cart:error', {
          bubbles: true,
          detail: { error },
        })
      );
    } finally {
      this._setLoading(false);
    }
  }

  /**
   * Show the component
   */
  _show() {
    this.removeAttribute('hidden');
    const section = this.closest('.rt-top-picks-cart-section');
    if (section) {
      section.removeAttribute('hidden');
    }
  }

  /**
   * Hide the component
   */
  _hide() {
    this.setAttribute('hidden', '');
    const section = this.closest('.rt-top-picks-cart-section');
    if (section) {
      section.setAttribute('hidden', '');
    }
  }

  /**
   * Set loading state
   * @param {boolean} isLoading
   */
  _setLoading(isLoading) {
    this._isLoading = isLoading;
    if (isLoading) {
      this.setAttribute('loading', '');
    } else {
      this.removeAttribute('loading');
    }
  }

  /**
   * Fetch recommendations for all product IDs
   */
  async _fetchAllRecommendations() {
    // Cancel any pending requests
    this._abortController?.abort();
    this._abortController = new AbortController();

    const allRecommendations = [];
    const seenProductIds = new Set();

    // Add cart product IDs to seen set to exclude them from recommendations
    // Normalize IDs to strings for consistent comparison
    this._productIds.forEach((id) => seenProductIds.add(String(id)));

    // Fetch recommendations for each product
    const fetchPromises = this._productIds.map((productId) =>
      this._fetchRecommendations(productId, this.LIMIT_PER_PRODUCT)
    );

    const results = await Promise.allSettled(fetchPromises);

    for (const result of results) {
      if (result.status === 'fulfilled' && result.value) {
        for (const product of result.value) {
          const productIdStr = String(product.id);
          // Skip products already in cart or already added to recommendations
          if (seenProductIds.has(productIdStr)) {
            continue;
          }
          seenProductIds.add(productIdStr);
          allRecommendations.push(product);
        }
      }
    }

    this._recommendations = allRecommendations;
  }

  /**
   * Fetch product recommendations from Shopify API
   * @param {string} productId - The product ID to get recommendations for
   * @param {number} limit - Maximum number of recommendations
   * @returns {Promise<object[]>} Array of recommended products
   */
  async _fetchRecommendations(productId, limit = 4) {
    const cacheKey = `${productId}-${limit}`;
    if (this._recommendationsCache.has(cacheKey)) {
      return this._recommendationsCache.get(cacheKey);
    }

    const rootUrl = window.Shopify?.routes?.root || '/';
    const url = `${rootUrl}recommendations/products.json?product_id=${productId}&limit=${limit}&intent=related`;

    const response = await fetch(url, {
      signal: this._abortController?.signal,
    });

    if (!response.ok) {
      if (response.status === 404) {
        console.warn(`Product ${productId} not found for recommendations`);
        return [];
      }
      throw new Error(`Failed to fetch recommendations: ${response.statusText}`);
    }

    const data = await response.json();
    const products = data.products || [];

    this._recommendationsCache.set(cacheKey, products);
    return products;
  }

  /**
   * Render all recommended products into the carousel
   */
  _renderProducts() {
    const container = this.querySelector('.rt-top-picks-cart__container');
    if (!container) {
      console.warn('Top picks carousel container not found');
      return;
    }

    container.innerHTML = '';

    if (this._recommendations.length === 0) {
      return;
    }

    for (const product of this._recommendations) {
      const slide = this._createProductSlide(product);
      container.appendChild(slide);
    }
  }

  /**
   * Create a product slide element
   * @param {object} product - The product object
   * @returns {HTMLElement}
   */
  _createProductSlide(product) {
    const slide = document.createElement('div');
    slide.className = 'rt-top-picks-cart__slide';

    const hasMultipleVariants = product.variants?.length > 1;
    const firstVariant = product.variants?.[0];

    slide.innerHTML = `
      <div class="rt-top-picks-card">
        <a
          href="${product.url}"
          class="rt-top-picks-card__image-link"
          data-rt-event="pdp_upsell: rt_pdp_upsell_click"
          data-rt-product-id="${product.id}"
          data-rt-product-handle="${product.handle}"
        >
          ${
            product.featured_image
              ? `<img
                  src="${this._getImageUrl(product.featured_image, '300x')}"
                  alt="${this._escapeHtml(product.title)}"
                  class="rt-top-picks-card__image"
                  loading="lazy"
                  width="300"
                  height="300"
                >`
              : '<div class="rt-top-picks-card__no-image">No Image</div>'
          }
        </a>
        <div class="rt-top-picks-card__content">
          <a
            href="${product.url}"
            class="rt-top-picks-card__title"
            data-rt-event="pdp_upsell: rt_pdp_upsell_click"
            data-rt-product-id="${product.id}"
            data-rt-product-handle="${product.handle}"
          >
            ${this._escapeHtml(product.title)}
          </a>
          ${this._createVariantSelector(product, hasMultipleVariants)}
          ${product.available ? this._createAddButton(product, firstVariant, hasMultipleVariants) : '<span class="rt-top-picks-card__sold-out">Sold Out</span>'}
        </div>
      </div>
    `;

    this._setupSlideEventListeners(slide, product);
    return slide;
  }

  /**
   * Create variant selector for products with multiple variants
   * @param {object} product
   * @param {boolean} hasMultipleVariants
   * @returns {string}
   */
  _createVariantSelector(product, hasMultipleVariants) {
    if (!hasMultipleVariants || !product.available) {
      return '';
    }

    const options = product.variants
      .filter((v) => v.available)
      .map((variant) => {
        const optionTitle = variant.title !== 'Default Title' ? variant.title : product.title;
        return `<option value="${variant.id}">${this._escapeHtml(optionTitle)} - ${this._escapeHtml(this._formatPrice(variant.price))}</option>`;
      })
      .join('');

    return `
      <div class="rt-top-picks-card__variant-selector">
        <select
          class="rt-top-picks-card__select"
          data-product-id="${product.id}"
          data-rt-event="pdp_upsell: rt_pdp_upsell_add_to_cart"
          data-rt-product-id="${product.id}"
          data-rt-product-handle="${product.handle}"
          data-rt-add-to-cart
          data-rt-variant
        >
          <option value="">Select an option</option>
          ${options}
        </select>
      </div>
    `;
  }

  /**
   * Create add to cart button
   * @param {object} product
   * @param {object} variant
   * @param {boolean} hasMultipleVariants
   * @returns {string}
   */
  _createAddButton(product, variant, hasMultipleVariants) {
    // No button needed for multi-variant products - select handles add-to-cart
    if (hasMultipleVariants) {
      return '';
    }

    return `
      <button
        type="button"
        class="rt-top-picks-card__add-btn"
        data-rt-event="pdp_upsell: rt_pdp_upsell_add_to_cart"
        data-rt-add-to-cart
        data-rt-variant="${variant?.id}"
        data-rt-variant-id="${variant?.id}"
        data-rt-product-id="${product.id}"
        data-rt-product-handle="${product.handle}"
        data-product-id="${product.id}"
      >
        ADD ${variant ? this._escapeHtml(this._formatPrice(variant.price)) : ''}
      </button>
    `;
  }

  /**
   * Setup event listeners for a slide
   * @param {HTMLElement} slide
   * @param {object} product
   */
  _setupSlideEventListeners(slide, product) {
    // Select elements with data-rt-add-to-cart are handled by the global event delegation
    // in raftel-theme.js, so no additional setup needed here
  }

  /**
   * Initialize drag-to-scroll functionality
   */
  _initDragScroll() {
    const container = this.querySelector('.rt-top-picks-cart__container');
    if (!container) {
      return;
    }

    this._dragContainer = container;

    this._onMouseDown = this._handleMouseDown.bind(this);
    this._onMouseMove = this._handleMouseMove.bind(this);
    this._onMouseUp = this._handleMouseUp.bind(this);
    this._onMouseLeave = this._handleMouseUp.bind(this);

    container.addEventListener('mousedown', this._onMouseDown);
    container.addEventListener('mousemove', this._onMouseMove);
    container.addEventListener('mouseup', this._onMouseUp);
    container.addEventListener('mouseleave', this._onMouseLeave);

    // Touch events for mobile
    this._onTouchStart = this._handleTouchStart.bind(this);
    this._onTouchMove = this._handleTouchMove.bind(this);
    this._onTouchEnd = this._handleMouseUp.bind(this);

    container.addEventListener('touchstart', this._onTouchStart, { passive: true });
    container.addEventListener('touchmove', this._onTouchMove, { passive: false });
    container.addEventListener('touchend', this._onTouchEnd);
  }

  /**
   * Cleanup drag scroll event listeners
   */
  _cleanupDragScroll() {
    const container = this._dragContainer;
    if (!container) {
      return;
    }

    container.removeEventListener('mousedown', this._onMouseDown);
    container.removeEventListener('mousemove', this._onMouseMove);
    container.removeEventListener('mouseup', this._onMouseUp);
    container.removeEventListener('mouseleave', this._onMouseLeave);
    container.removeEventListener('touchstart', this._onTouchStart);
    container.removeEventListener('touchmove', this._onTouchMove);
    container.removeEventListener('touchend', this._onTouchEnd);
  }

  /**
   * Handle mouse down for drag start
   * @param {MouseEvent} e
   */
  _handleMouseDown(e) {
    this._isDragging = true;
    this._startX = e.pageX - this._dragContainer.offsetLeft;
    this._scrollLeft = this._dragContainer.scrollLeft;
    this._dragContainer.classList.add('is-dragging');
  }

  /**
   * Handle mouse move for dragging
   * @param {MouseEvent} e
   */
  _handleMouseMove(e) {
    if (!this._isDragging) {
      return;
    }
    e.preventDefault();
    const x = e.pageX - this._dragContainer.offsetLeft;
    const walk = (x - this._startX) * 1.5; // Scroll speed multiplier
    this._dragContainer.scrollLeft = this._scrollLeft - walk;
  }

  /**
   * Handle mouse up / leave for drag end
   */
  _handleMouseUp() {
    this._isDragging = false;
    this._dragContainer?.classList.remove('is-dragging');
  }

  /**
   * Handle touch start for mobile drag
   * @param {TouchEvent} e
   */
  _handleTouchStart(e) {
    this._isDragging = true;
    this._startX = e.touches[0].pageX - this._dragContainer.offsetLeft;
    this._scrollLeft = this._dragContainer.scrollLeft;
    this._dragContainer.classList.add('is-dragging');
  }

  /**
   * Handle touch move for mobile dragging
   * @param {TouchEvent} e
   */
  _handleTouchMove(e) {
    if (!this._isDragging) {
      return;
    }
    const x = e.touches[0].pageX - this._dragContainer.offsetLeft;
    const walk = (x - this._startX) * 1.5;
    this._dragContainer.scrollLeft = this._scrollLeft - walk;
  }

  /**
   * Refresh the component with new product IDs
   * @param {string[]} productIds - Array of product IDs
   */
  refresh(productIds) {
    this.setAttribute('data-product-ids', productIds.join(','));
  }

  /**
   * Get the image URL with size
   * @param {string} src - Original image URL
   * @param {string} size - Size string (e.g., '300x')
   * @returns {string}
   */
  _getImageUrl(src, size) {
    if (!src) {
      return '';
    }

    if (src.includes('cdn.shopify.com')) {
      return src.replace(/(\.[^.]+)$/, `_${size}$1`);
    }

    return src;
  }

  /**
   * Escape HTML special characters
   * @param {string} str
   * @returns {string}
   */
  _escapeHtml(str) {
    if (!str) {
      return '';
    }
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
}

// Register the custom element
if (!customElements.get(RtTopPicksCart.tagName)) {
  customElements.define(RtTopPicksCart.tagName, RtTopPicksCart);
}

export { RtTopPicksCart };
